import { useEffect, useRef, type ReactNode } from 'react';

type Node = {
  layer: number;
  x: number;
  y: number;
  jx: number;
  jy: number;
  amp: number;
};

const LAYERS = [1, 3, 5, 8, 12];

function buildTree(): { nodes: Node[]; edges: [Node, Node][] } {
  const nodes: Node[] = [];
  const edges: [Node, Node][] = [];
  let previous: Node[] = [];

  LAYERS.forEach((count, layer) => {
    const row: Node[] = [];
    for (let i = 0; i < count; i += 1) {
      const node: Node = {
        layer,
        x: count === 1 ? 0.5 : (i + 0.5) / count,
        y: 0.11 + layer * 0.195,
        jx: Math.random() * Math.PI * 2,
        jy: Math.random() * Math.PI * 2,
        amp: 0.004 + Math.random() * 0.005,
      };
      nodes.push(node);
      row.push(node);
      if (previous.length > 0) {
        edges.push([previous[Math.floor((i * previous.length) / count)]!, node]);
      }
    }
    previous = row;
  });

  return { nodes, edges };
}

/**
 * 左侧氛围区。背景那张缓慢浮动的节点图就是这套系统的主题——组织架构。
 *
 * `depth` 表示点亮到第几层：初始化每往前走一步多亮一层，走完整棵树都是暖铜色。
 */
export function AtmospherePanel({
  depth,
  kicker,
  statement,
  sub,
  steps,
}: {
  depth: number;
  kicker?: string;
  statement: string;
  sub?: string;
  /** [当前第几步（从 1 起）, 共几步]；不传就不显示进度条。 */
  steps?: [number, number];
}): ReactNode {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas === null) return;
    const ctx = canvas.getContext('2d');
    if (ctx === null) return;

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const tree = buildTree();
    let width = 0;
    let height = 0;
    let time = 0;
    let frame = 0;

    const resize = (): void => {
      const rect = canvas.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) return;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      width = rect.width;
      height = rect.height;
      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    // 已点亮的层不透明，下一层微微透出，更远的几乎看不见——暗示「还没走到」。
    const visibility = (layer: number): number => {
      if (layer <= depth) return 1;
      if (layer === depth + 1) return 0.26;
      return 0.07;
    };

    const positionOf = (node: Node): { x: number; y: number } => ({
      x: (0.1 + node.x * 0.8) * width + Math.sin(time * 0.5 + node.jx) * node.amp * width,
      y: node.y * height + Math.cos(time * 0.42 + node.jy) * node.amp * height,
    });

    const draw = (): void => {
      if (width === 0 || height === 0) {
        resize();
        if (width === 0 || height === 0) return;
      }
      ctx.clearRect(0, 0, width, height);

      for (const [from, to] of tree.edges) {
        const a = positionOf(from);
        const b = positionOf(to);
        const v = Math.min(visibility(from.layer), visibility(to.layer));
        ctx.strokeStyle = `rgba(126,168,194,${0.16 * v})`;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(a.x, a.y);
        ctx.bezierCurveTo(a.x, (a.y + b.y) / 2, b.x, (a.y + b.y) / 2, b.x, b.y);
        ctx.stroke();
      }

      for (const node of tree.nodes) {
        const p = positionOf(node);
        const v = visibility(node.layer);
        const lit = node.layer <= depth;
        const r = node.layer === 0 ? 4.5 : 3;
        if (lit) {
          ctx.fillStyle = `rgba(210,169,60,${0.1 * v})`;
          ctx.beginPath();
          ctx.arc(p.x, p.y, r * 3.4, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.fillStyle = lit ? `rgba(210,169,60,${0.62 * v})` : `rgba(126,168,194,${0.4 * v})`;
        ctx.beginPath();
        ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
        ctx.fill();
      }
    };

    const loop = (): void => {
      time += 0.012;
      draw();
      frame = requestAnimationFrame(loop);
    };

    const observer = new ResizeObserver(() => {
      resize();
      draw();
    });
    observer.observe(canvas);
    resize();

    if (reduced) draw();
    else loop();

    return () => {
      observer.disconnect();
      if (frame !== 0) cancelAnimationFrame(frame);
    };
  }, [depth]);

  const [current, total] = steps ?? [0, 0];

  return (
    <aside className="panel">
      <canvas ref={canvasRef} aria-hidden="true" />
      <span className="wordmark">Capsio Hub</span>
      <div className="panel-foot">
        <div>
          {kicker !== undefined && <p className="kicker">{kicker}</p>}
          <p className="statement">{statement}</p>
          {sub !== undefined && <p className="sub">{sub}</p>}
        </div>
        {steps !== undefined ? (
          <div className="pips">
            {Array.from({ length: total }, (_unused, index) => (
              <i
                key={index}
                className={index + 1 < current ? 'past' : index + 1 === current ? 'on' : ''}
              />
            ))}
          </div>
        ) : (
          <div />
        )}
      </div>
    </aside>
  );
}
